<div class="container py-4">
  <h2>{{ 'ADMIN.MAIL_ACCOUNTS.TITLE' | translate }}</h2>

  <div class="row g-2 mt-2 align-items-end">
    <div class="col-md-3">
      <app-text-search
        [value]="searchTerm"
        (valueChange)="searchTerm = $event ?? ''"
        [labelKey]="'ADMIN.MAIL_ACCOUNTS.FILTER_NAME_HOST_LABEL'"
        [placeholderKey]="'ADMIN.MAIL_ACCOUNTS.FILTER_NAME_HOST_PLACEHOLDER'"
        [showClearButton]="true"
        inputId="admin-mail-accounts-search"
        [disabled]="listLoading"
      />
    </div>
    <div class="col-md-3">
      <app-form-select
        id="filter-owner"
        [formGroup]="filterForm"
        controlName="ownerIds"
        [label]="'ADMIN.MAIL_ACCOUNTS.FILTER_OWNER_LABEL'"
        [options]="users"
        [optionLabelSelector]="[{ selector: 'username' }]"
        optionValueSelector="id"
        [multiple]="true"
        [filter]="true"
        [clear]="true"
        [placeholder]="'ADMIN.MAIL_ACCOUNTS.FILTER_OWNER_PLACEHOLDER'"
      />
    </div>
    <div class="col-md-2">
      <label class="form-label mb-0 small">&nbsp;</label>
      <app-mdb-button
        [text]="'ADMIN.APPLY'"
        color="primary"
        [outlined]="true"
        customClasses="w-100 d-block"
        [disabled]="listLoading"
        (onClick)="applyFilters()"
      />
    </div>
    <div class="col-md-4 d-flex justify-content-end gap-2 align-items-end">
      <app-mdb-button
        [text]="'ADMIN.MAIL_ACCOUNTS.ADD_BUTTON'"
        color="primary"
        [disabled]="listLoading"
        (onClick)="openCreateModal()"
      />
    </div>
  </div>

  @if (errorMessage) {
    <div class="alert alert-danger mt-2 mb-0" role="alert">{{ errorMessage | translate }}</div>
  }

  <div class="table-responsive mt-4">
    <table class="table table-sm">
      <thead>
        <tr>
          <th scope="col">{{ 'ADMIN.MAIL_ACCOUNTS.NAME' | translate }}</th>
          <th scope="col">{{ 'ADMIN.MAIL_ACCOUNTS.HOST' | translate }}</th>
          <th scope="col">{{ 'ADMIN.MAIL_ACCOUNTS.OWNER' | translate }}</th>
          <th scope="col">{{ 'ADMIN.MAIL_ACCOUNTS.LAST_IMPORT' | translate }}</th>
          <th scope="col"></th>
        </tr>
      </thead>
      <tbody>
        @if (listLoading) {
          <tr>
            <td colspan="5" class="text-center py-4">
              <app-spinner></app-spinner>
              <span class="ms-2">{{ 'LOADING_RESULTS' | translate }}</span>
            </td>
          </tr>
        } @else if (mailAccounts.length === 0) {
          <tr>
            <td colspan="5" class="text-center text-muted py-4">{{ 'NO_RESULTS_FOUND' | translate }}</td>
          </tr>
        } @else {
          @for (account of mailAccounts; track account.id) {
            <tr>
              <td>{{ account.name }}</td>
              <td>{{ account.host }}</td>
              <td>{{ ownerName(account) }}</td>
              <td class="stats-cell-td">
                <span
                  class="stats-cell position-relative d-inline-flex align-items-center gap-1"
                  (mouseenter)="onStatsCellMouseEnter(account.id)"
                  (mouseleave)="onStatsCellMouseLeave()"
                >
                  {{ lastImportDisplay(account) }}
                  <i
                    class="fas fa-info-circle text-muted cursor-pointer ms-1"
                    [attr.aria-label]="'MAILARCHIV.INFO' | translate"
                  ></i>
                  @if (hoveredStatsAccountId === account.id) {
                    <div class="stats-popover shadow-sm border rounded p-2 small bg-white">
                      @if (statsByAccountId[account.id] === undefined || statsByAccountId[account.id] === 'loading') {
                        <span class="text-muted">{{ 'LOADING' | translate }}</span>
                      } @else if (statsByAccountId[account.id] === 'error') {
                        <span class="text-danger">{{ 'MAILARCHIV.STATS_CONNECTION_ERROR' | translate }}</span>
                      } @else if (getAccountStatsDisplay(account.id); as stats) {
                        <div class="stats-popover-content">
                          <div>{{ 'MAILARCHIV.LAST_IMPORT' | translate }}: {{ stats.lastImportInfo ?? '–' }}</div>
                          <div>{{ 'MAILARCHIV.MAILS_TOTAL' | translate }}: {{ stats.numberOfDocuments }}</div>
                          <div>{{ 'MAILARCHIV.NEWEST_MAIL' | translate }}: {{ stats.newestMailDate ?? '–' }}</div>
                          <div>{{ 'MAILARCHIV.OLDEST_MAIL' | translate }}: {{ stats.oldestMailDate ?? '–' }}</div>
                        </div>
                      } @else {
                        <span class="text-muted">{{ 'LOADING' | translate }}</span>
                      }
                    </div>
                  }
                </span>
              </td>
              <td>
                <div class="d-flex gap-2">
                  <app-mdb-button
                    [text]="'ADMIN.MAIL_ACCOUNTS.IMPORT_BUTTON'"
                    color="secondary"
                    [outlined]="true"
                    size="sm"
                    [loading]="importingAccountId === account.id"
                    [disabled]="importingAccountId !== null"
                    (onClick)="triggerImport(account)"
                  />
                  <app-mdb-button
                    [text]="'EDIT'"
                    color="primary"
                    [outlined]="true"
                    size="sm"
                    (onClick)="openEditModal(account)"
                  />
                  <app-mdb-button
                    [text]="'ADMIN.MAIL_ACCOUNTS.DELETE'"
                    color="danger"
                    [outlined]="true"
                    size="sm"
                    [loading]="deletingAccountId === account.id"
                    [disabled]="deletingAccountId !== null"
                    (onClick)="openDeleteConfirmModal(account)"
                  />
                </div>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>

  <app-list-pagination-bar
    [meta]="paginationMeta"
    [perPageOptions]="perPageOptions"
    [loading]="listLoading"
    selectId="per-page-mail-accounts"
    (perPageChange)="onPerPageChange($event)"
    (previousClicked)="previousPage()"
    (nextClicked)="nextPage()"
  />
</div>
